import {
  ISelectionAction,
  SelectionActionType,
  ISelectionSetAction,
} from '@engoo/realtime-protocol/selection';
import { Collection, Map } from 'immutable';
import { IKeyPath } from '@engoo/realtime-protocol/keypath';
import { AbortedError } from 'app/constants/ResponseErrors';
import { notify } from 'app/services/errorReporter';
import { useCallback, useRef } from 'react';
import { SharedSelectionDocumentManager } from 'app/models/rtc/selection/documentSpec';
import { useCallbackOne } from 'app/utils/useMemoOne';

const isSetAction = (action: ISelectionAction): action is ISelectionSetAction =>
  action.type === SelectionActionType.Set;

export interface ISharedSelectionAreaUpdater {
  updateRemote: (selection: ISelectionAction) => void;
  unregisterArea: () => void;
}

export interface ISharedSelectionUpdater {
  addArea: () => ISharedSelectionAreaUpdater;
}

const getSetSelections = (
  selections: Map<symbol, ISelectionAction>,
) => {
  return selections.valueSeq().filter(isSetAction);
};

const compareSelections = (
  a: ISelectionSetAction,
  b: ISelectionSetAction,
) => {
  const aIsLast = a.endOffset !== -1;
  const aIsFirst = a.startOffset !== 0;
  const bIsFirst = b.startOffset !== 0;
  const bIsLast = b.endOffset !== -1;

  if (aIsLast || bIsFirst) {
    return 1;
  } else if (aIsFirst || bIsLast) {
    return -1;
  }
  return 0;
};

const mergeSelections = (
  toMergeSelections: Collection<number, ISelectionSetAction>,
): ISelectionAction => {
  const sortedSelections = toMergeSelections.sort(compareSelections);

  if (sortedSelections.count() === 0) {
    return { type: SelectionActionType.Clear };
  }

  const first = sortedSelections.first();
  const last = sortedSelections.last();

  if (!first || !last) {
    return { type: SelectionActionType.Clear };
  }

  const startOffset = first.startOffset;
  const endOffset = last.endOffset;
  const paths = sortedSelections.reduce((res: IKeyPath[], selection: ISelectionSetAction) => {
    return [...res, ...selection.paths];
  }, []);

  const mergedState = {
    color: first.color,
    paths,
    type: SelectionActionType.Set,
    startOffset,
    endOffset,
    cursorPosition: first.cursorPosition,
  };

  return mergedState;
};

/**
 * WARNING: Package-private hook.
 * Use useSharedSelectionContext instead
 */
export const useSharedSelectionUpdater = (
  manager: SharedSelectionDocumentManager | undefined,
  isBlocked: boolean,
): ISharedSelectionUpdater => {
  const selections = useRef(Map<symbol, ISelectionAction>());

  const updateDocument = useCallback(() => {
    if (!manager || isBlocked) {
      return;
    }

    const toMergeSelections = getSetSelections(selections.current);
    const action = mergeSelections(toMergeSelections);

    try {
      manager.addLocalAction(action);
    } catch (error) {
      // If the connection is dropped when the action is being send
      // out, it'll throw AbortedError
      if (!(error instanceof AbortedError)) {
        notify(error, { name: 'SharedSelectionUpdater.updateDocument' });
      }
    }
  }, [manager, isBlocked]);

  const addArea = useCallbackOne(() => {
    const rootId = Symbol();

    selections.current = selections.current.set(rootId, { type: SelectionActionType.Clear });

    return {
      updateRemote: (selection: ISelectionAction) => {
        selections.current = selections.current.set(rootId, selection);
        updateDocument();
      },
      unregisterArea: () => {
        selections.current = selections.current.delete(rootId);
        updateDocument();
      },
    };
  }, [updateDocument]);

  return { addArea };
};
